export interface SubscriptionPlanFeature {
  name: string;
  included: boolean;
}

export interface TokenPackConfig {
  name: string;
  tokenAmount: number;
  price: number;
  priceId: string;
}

export interface SubscriptionPlanConfig {
  id: string;
  name: string;
  price: number;
  tokenAllowance: number;
  features: SubscriptionPlanFeature[];
}

export const SUBSCRIPTION_PLANS = {
  FREE: {
    id: 'price_free',
    name: 'Free Trial',
    price: 0,
    tokenAllowance: 0,
    features: [
      { name: '7 Day Full Access Trial', included: true },
      { name: 'Daily Tarot Card Draw', included: true },
      { name: 'Blind Readings', included: true },
      { name: 'Question Readings', included: true },
      { name: 'Astrological Insights', included: true },
    ],
  },
  PRO: {
    id: process.env.STRIPE_PRO_PRICE_ID!,
    name: 'Pro',
    price: 19.99,
    tokenAllowance: 500000,
    features: [
      { name: 'Unlimited Access', included: true },
      { name: 'Daily Tarot Card Draw', included: true },
      { name: 'Blind Readings', included: true },
      { name: 'Question Readings', included: true },
      { name: 'Astrological Insights', included: true },
    ],
  },
} as const satisfies Record<string, SubscriptionPlanConfig>;

export const TOKEN_PACKS = {
  SMALL: {
    name: 'Small Pack',
    tokenAmount: 10000, // 10,000 tokens
    price: 4.99,
    priceId: process.env.STRIPE_SMALL_TOKEN_PACK_PRICE_ID!,
  },
  MEDIUM: {
    name: 'Medium Pack',
    tokenAmount: 50000, // 50,000 tokens
    price: 9.99,
    priceId: process.env.STRIPE_MEDIUM_TOKEN_PACK_PRICE_ID!,
  },
  LARGE: {
    name: 'Large Pack',
    tokenAmount: 150000, // 150,000 tokens
    price: 19.99,
    priceId: process.env.STRIPE_LARGE_TOKEN_PACK_PRICE_ID!,
  },
} as const;

export type SubscriptionPlanId = keyof typeof SUBSCRIPTION_PLANS;
export type SubscriptionPlan = typeof SUBSCRIPTION_PLANS[SubscriptionPlanId];
export type TokenPackKey = keyof typeof TOKEN_PACKS;

export const getTokenAllowanceForPlan = (planId: SubscriptionPlanId): number => {
  return SUBSCRIPTION_PLANS[planId].tokenAllowance;
};

export const getPlanIdFromPrice = (priceId: string): SubscriptionPlanId | null => {
  const plan = Object.entries(SUBSCRIPTION_PLANS).find(([_, plan]) => plan.id === priceId);
  return plan ? (plan[0] as SubscriptionPlanId) : null;
};

export const getPlanFromPrice = (priceId: string): SubscriptionPlan | null => {
  const planId = getPlanIdFromPrice(priceId);
  return planId ? SUBSCRIPTION_PLANS[planId] : null;
};

export function getTokenPackFromPrice(priceId: string): TokenPackConfig | null {
  const pack = Object.values(TOKEN_PACKS).find(pack => pack.priceId === priceId);
  return pack || null;
};
